<script context="module" lang="ts">
	function getSelected(
		slug: string,
		menuItems: any[],
		menuItemsIsSortedForSearch = false
	): any[] {
		let ret = [];

		const items = menuItemsIsSortedForSearch
			? menuItems
			: sortItems(menuItems);

		for (const item of items) {
			if (!item) {
				continue;
			}

			if (slug.indexOf(item.url) === 0) {
				ret.push(item);

				if (item.childs) {
					for (const child of item.childs) {
						if (!child) {
							continue;
						}

						if (slug.indexOf(child.url) === 0 && child.color) {
							ret.push(child);
							break;
						}
					}
				}

				break;
			}
		}

		return ret;
	}

	export function slugToColor(
		forSlug: string,
		menuItems: any[],
		ret: "hex" | "rgba" = "hex",
		fallbackColor: string = "#FFE9F1"
	) {
		try {
			if (!forSlug || !menuItems || menuItems.length === 0) {
				return fallbackColor;
			}

			let color = fallbackColor;

			const x = getSelected(
				forSlug.indexOf("/") === 0 ? forSlug : `/${forSlug}`,
				menuItems
			);

			for (const item of x) {
				if (item.color) {
					color = item.color;
				}
			}

			return ret === "hex" ? color : convertHexToRGBA(color);
		} catch (e) {
			console.error(e, menuItems);
		}
	}

	export const convertHexToRGBA = (hexCode: string, opacity: number = 1) => {
		let hex = hexCode.replace("#", "");

		if (hex.length === 3) {
			hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
		}

		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);

		if (opacity > 1 && opacity <= 100) {
			opacity = opacity / 100;
		}

		return `rgba(${r},${g},${b},${opacity})`;
	};

	export function hasSubmenu(path: string, menuItems: any[]): any {
		if (!menuItems || menuItems.length === 0) {
			return false;
		}

		for (const item of menuItems) {
			if (path.indexOf(item.url) === 0) {
				return item.childs;
			}
		}
	}

	export function sortItems(items: any[]): any[] {
		if (!items || items.length === 0) {
			return [];
		}

		const ret = [...items].sort(
			(a: any, b: any) => b.url?.length - a.url?.length
		);

		return ret.map((item: any) => {
			if (item.childs) {
				item.childs = item.childs.sort(
					(a: any, b: any) => b.url?.length - a.url?.length
				);
			}
			return item;
		});
	}
</script>

<script lang="ts">
	import { onMount, getContext } from "svelte";
	import Hydrator from "@reperio-news/core.components/Hydrator.svelte";
	import { fade } from "svelte/transition";

	// @ts-ignore
	import searchIcon from "./search.svg";
	// @ts-ignore
	import menuIcon from "./menuIcon.svg";
	// @ts-ignore
	import chevronDown from "./chevron-down.svg";
	// @ts-ignore
	import closeIcon from "./closeIcon.svg";

	export let id: string;
	export let globalId: string;
	export let globalSource: number;
	// ignore no use
	id || globalId || globalSource;

	// simplified context
	export let context: any;
	const c: SvelteStore<any> = getContext("context");

	if (!context) {
		context = $c;
	}

	export let data = {
		items: [],
	};

	let scrollY: number;
	let menuTop = 0;
	let menuVisible = false;
	let menuDom: HTMLDivElement;
	let menuItemsDom: HTMLDivElement;
	let dropdownDom: HTMLDivElement;
	let showDropdown = false;
	let containerDom: HTMLDivElement;
	let menuOverlayDom: HTMLDivElement;
	let searchFormDom: HTMLFormElement;
	let showSearch = false;
	let fakeDom: HTMLDivElement;

	$: if (showDropdown && containerDom && dropdownDom && menuOverlayDom) {
		let x = containerDom.getBoundingClientRect();

		dropdownDom.style.top = `${x.top + x.height}px`;
		dropdownDom.style.left = `${Math.max(
			x.left + x.width - dropdownDom.offsetWidth,
			0
		)}px`;

		dropdownDom.style.height = `${
			window.innerHeight - dropdownDom.getBoundingClientRect().top - 35
		}px`;

		menuOverlayDom.style.top = dropdownDom.style.top = `${
			x.top + x.height
		}px`;
		menuOverlayDom.style.left = "0px";
	}

	$: if (showSearch && searchFormDom) {
		searchFormDom.querySelector("input").focus();
	}

	function onScroll(_scrollY: number) {
		if (fakeDom && scrollY) {
			menuTop = Math.max(fakeDom.getBoundingClientRect().top, 0);
		}
	}

	$: onScroll(scrollY);

	let color = context.domain_config.MISC.menuBorderColor;
	let hoverColor: string | null = null;

	let sortedItems: any;
	let selected: any;
	let items: any[];

	$: if (data && data.items && context) {
		sortedItems = sortItems(data.items);

		items = data.items;
		if (context.path !== "/") {
			selected = getSelected(context.path, sortedItems, true)?.pop();
			if (selected) {
				items = items.filter(
					(item) => item && item.url !== selected.url
				);
				selected.selected = true;
				items.unshift(selected);
			}
		}

		if (context.path !== "/") {
			color = slugToColor(
				context.path,
				sortedItems,
				"hex",
				context.domain_config.MISC.menuBorderColor
			);
		}
	}

	function toggleSubItems(e: MouseEvent) {
		const parent = (<HTMLElement>e.target).parentNode
			.parentNode as HTMLLIElement;
		parent.classList.toggle("collapsed");
	}

	let isMobile = false;
	let menuIsOverflowing = false;
	let mounted = false;

	function checkOverflow(_m?: boolean, _dom?: any) {
		if (!menuItemsDom) return;
		const itemsWidth = [...menuItemsDom.querySelectorAll("a")]
			.map((item) => item.offsetWidth)
			.reduce((a, b) => a + b + 6, 0);
		menuIsOverflowing = itemsWidth > menuItemsDom.offsetWidth;
	}

	//	menuItemsDom in the statement so it re-runs once Hydrator's
	//	post-mount branch swap re-binds the container
	$: checkOverflow(mounted && !!menuItemsDom, menuItemsDom);

	// cls related bs — reactive on the doms so it re-runs (and the menu
	// still un-hides) if the Hydrator swap re-binds them after mount
	$: if (mounted && menuDom && fakeDom) {
		setTimeout(() => {
			if (!fakeDom) {
				return;
			}
			menuVisible = true;
			menuTop = fakeDom.getBoundingClientRect().top;
		}, 1);
	}

	onMount(() => {
		isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			);

		mounted = true;
		window.addEventListener("resize", checkOverflow);
	});
</script>

<Hydrator props={$$props} component="TopMenu" hydrate={true}>
	<div class="fake" bind:this={fakeDom} />

	<div
		bind:this={menuDom}
		class="menu"
		class:admin={__ADMIN__}
		style:display={menuVisible ? "flex" : "none"}
		style:top="{menuTop}px"
		style:--menuBorderColor={convertHexToRGBA(
			hoverColor || color,
			hoverColor ? 0.6 : 0.3
		)}
		style="--bgColor: {context.domain_config.MISC.menuDefaultcolor};"
	>
		<div
			bind:this={containerDom}
			class="container"
			style=" --menuFontColor: {context.domain_config.MISC
				.menuFontColor}; --ratio: {context.domain_config.MISC.logo
				.width / context.domain_config.MISC.logo.height};"
		>
			<a
				href="/"
				title={context.domain_config.default.friendlyName}
				class="logo"
			>
				<img
					src="/logo.png"
					alt={context.domain_config.default.friendlyName}
					width={context.domain_config.MISC.logo.width}
					height={context.domain_config.MISC.logo.height}
				/>
			</a>

			{#if showSearch}
				<form
					bind:this={searchFormDom}
					method="get"
					action="/search"
					autocomplete="off"
				>
					<input
						type="search"
						name="q"
						placeholder="Caută în {context.domain_config.default
							.friendlyName}"
					/>
				</form>
			{:else}
				<div class="items" bind:this={menuItemsDom}>
					{#if items}
						{#each items as item}
							<a
								class:selected={item.selected}
								href={item.url}
								target={item.target}
								style="--color:{item.color ||
									context.domain_config.MISC
										.menuBorderColor};"
								on:mouseenter={() =>
									(hoverColor =
										item.color ||
										context.domain_config.MISC
											.menuBorderColor)}
								on:mouseleave={() =>
									(hoverColor = null)}
								>{item.name}</a
							>
						{/each}
					{/if}
				</div>
			{/if}

			{#if menuIsOverflowing && !showSearch}
				<div
					class="menuButton"
					style="margin-left: auto"
					on:click={() => (showDropdown = !showDropdown)}
				>
					...
				</div>
			{/if}

			<div
				class="menuButton"
				on:click={() => {
					if (!showSearch) {
						showSearch = true;
					} else {
						searchFormDom.submit();
					}
				}}
			>
				<img
					class="menuButton"
					src={searchIcon}
					alt="search"
					width="32"
					height="32"
				/>
			</div>

			{#if !showSearch}
				<div
					class="menuButton"
					class:menuButtonSelected={showDropdown}
					on:click={() => (showDropdown = !showDropdown)}
				>
					<img
						class="menuButton"
						src={menuIcon}
						alt="menu"
						width="32"
						height="32"
					/>
				</div>
			{:else}
				<div class="menuButton">
					<img
						src={closeIcon}
						alt="Închide"
						width="32"
						height="32"
						on:click={() => (showSearch = false)}
					/>
				</div>
			{/if}
		</div>

		<div class="other">
			<a
				class="social-facebook"
				href="//www.facebook.com/OKmagazineRomania/"
				title="urmărește-ne pe facebook"
				target="_blank"
			>
				<span class="icon facebook">
					<svg width="28" height="28" viewBox="0 0 28 28">
						><title>facebook icon</title><use
							xmlns:xlink="http://www.w3.org/1999/xlink"
							xlink:href="/icons.svg#icon-facebook"
						/></svg
					>
				</span>
			</a>
			<a
				class="social-instagram"
				href="//www.instagram.com/okmagazineromania/"
				title="urmărește-ne pe instagram"
				target="_blank"
			>
				<span class="icon instagram">
					<svg width="28" height="28" viewBox="0 0 28 28">
						><title>instagram icon</title><use
							xmlns:xlink="http://www.w3.org/1999/xlink"
							xlink:href="/icons.svg#icon-instagram"
						/></svg
					>
				</span>
			</a>
			<a
				class="social-youtube"
				href="https://www.youtube.com/channel/UCOl5vlVkpcGOMjpoCYAGwaQ"
				title="urmărește-ne pe youtube"
				target="_blank"
			>
				<span class="icon youtube">
					<svg width="28" height="28" viewBox="0 0 28 28">
						><title>youtube icon</title><use
							xmlns:xlink="http://www.w3.org/1999/xlink"
							xlink:href="/icons.svg#icon-youtube"
						/></svg
					>
				</span>
			</a>
		</div>
	</div>

	{#if showDropdown}
		<div
			transition:fade={{ duration: 100 }}
			bind:this={dropdownDom}
			class="menuDropdown"
			style="--bgColor: {context.domain_config.MISC.menuDefaultcolor}"
		>
			{#if data && data.items}
				<ul>
					{#each data.items as item}
						<li
							class="mainItem collapsed"
							style="--color: {item.color}"
						>
							<div>
								<a href={item.url} target={item.target}
									>{item.name}</a
								>
								{#if item.childs && item.childs.length !== 0}
									<img
										class="toggleSub"
										src={chevronDown}
										width="32"
										height="32"
										alt="Mai multe"
										on:click={toggleSubItems}
									/>
								{/if}
							</div>
							{#if item.childs && item.childs.length !== 0}
								<ul>
									{#each item.childs as subItem}
										<li
											class="subItem"
											style="--color: {item.color}"
										>
											<a
												href={subItem.url}
												target={subItem.target}
											>
												{subItem.name}
											</a>
										</li>
									{/each}
								</ul>
							{/if}
						</li>
					{/each}
				</ul>
			{/if}
		</div>

		<div
			class="menuOverlay"
			bind:this={menuOverlayDom}
			on:click={() => (showDropdown = false)}
		/>
	{/if}
</Hydrator>

<svelte:window bind:scrollY />

<svelte:head>
	<meta name="theme-color" content={color} />
</svelte:head>

<style lang="scss">
	@property --menuBorderColor {
		syntax: "<color>";
		inherits: true;
		initial-value: transparent;
	}

	.fake {
		height: 66px;
	}

	.admin {
		position: relative !important;
	}

	.menu {
		position: fixed;
		left: 0px;
		top: 0px;
		z-index: 5;

		user-select: none;
		width: 100%;
		display: none;
		background-color: var(--bgColor);
		padding: 0;
		overflow: hidden;
		border-bottom: 2px solid;
		border-image: linear-gradient(
				to right,
				transparent,
				var(--menuBorderColor),
				transparent
			)
			1;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
		transition: --menuBorderColor 500ms cubic-bezier(0.4, 0, 0.2, 1);

		.other {
			display: flex;
			justify-content: center;
			align-items: center;

			a {
				margin-right: 10px;
				opacity: 0.4;
				transition: opacity 250ms ease;
			}

			a:hover {
				opacity: 1;
			}

			a.social-facebook {
				svg {
					fill: #1c468f;
				}
			}

			a.social-youtube {
				svg {
					fill: #f00;
				}
			}
		}

		.container {
			width: 100%;
			margin: auto;
			max-width: 1000px;
			height: 64px;
			display: flex;
			flex-direction: row;
			flex-wrap: nowrap;
			overflow: hidden;
			align-items: center;
			padding: 0 12px;

			a {
				margin-right: 6px;
				height: 64px;
				display: flex;
				justify-content: center;
				align-items: center;
				font-style: normal;
				font-weight: 500;
				font-size: 13px;
				letter-spacing: 1.2px;
				text-transform: uppercase;
				white-space: nowrap;
				color: var(--menuFontColor);
				padding: 0 8px;
				border-bottom: 3px solid transparent;
				transition: border-color 250ms ease;

				&::before {
					content: "";
					display: inline-block;
					width: 5px;
					height: 5px;
					border-radius: 50%;
					background-color: var(--color);
					margin-right: 6px;
					flex-shrink: 0;
					opacity: 0.6;
					transition: opacity 250ms ease;
				}

				&:hover::before {
					opacity: 1;
				}
			}

			a.selected {
				font-weight: 700;
				border-bottom: 3px solid var(--color);
			}

			a:hover {
				border-bottom: 3px solid var(--color) !important;
			}

			a.logo,
			a.logo:hover,
			a.logo.selected {
				height: 50px;
				width: calc(50px * var(--ratio));
				margin-right: 16px;
				border-right: 1px solid #e0e0e0;
				border-bottom: none !important;
				padding-right: 16px;
				transition: none;

				&::before {
					display: none;
				}

				img {
					object-fit: contain;
					width: 100%;
					height: 100%;
				}
			}

			.menuButton {
				letter-spacing: 3px;
				margin-left: 12px;
				font-style: normal;
				font-weight: 500;
				font-size: 14px;
				color: var(--menuFontColor);
				min-width: 32px;
				min-height: 32px;
				display: flex;
				align-items: center;
				cursor: pointer;
				opacity: 0.7;
				transition: opacity 250ms ease;

				&:hover {
					opacity: 1;
				}

				img {
					margin-left: auto;
				}
			}

			.menuButtonSelected {
				background-color: rgb(234, 240, 222);
				border-radius: 5px;
				opacity: 1;
			}

			.items {
				display: flex;
				flex-direction: row;
				flex-wrap: wrap;
				overflow: hidden;
				height: 64px;
				align-items: center;
				--rmx: calc(var(--ratio) * 50px + 100px);
				width: calc(100% - var(--rmx));
			}

			form {
				display: flex;
				flex-direction: row;
				flex-wrap: nowrap;
				overflow: hidden;
				height: 53px;
				align-items: center;
				--rmx: calc(var(--ratio) * 50px + 60px);
				width: calc(100% - var(--rmx));

				input {
					background-color: #f5f5f5;
					width: calc(100% - 20px);
					height: 40px;
					border: 1px solid #e0e0e0;
					text-align: center;
					border-radius: 8px;
					outline: none;
					font-size: 14px;
					font-family: inherit;
					letter-spacing: 0.5px;
					transition: border-color 250ms ease;

					&:focus {
						border-color: var(--menuBorderColor);
					}
				}
			}
		}
	}

	.menuDropdown {
		position: fixed;
		width: 360px;
		padding: 12px 16px;
		min-height: 200px;
		background-color: var(--bgColor);
		z-index: 10;
		display: flex;
		flex-direction: column;
		overflow-y: auto;
		box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

		ul {
			display: flex;
			flex-direction: column;
			width: 100%;
			margin: 0;
			padding: 0;
			gap: 2px;

			a {
				display: flex;
				align-items: center;
				height: 38px;
				font-size: 13px;
				font-weight: 500;
				letter-spacing: 1px;
				text-transform: uppercase;
				color: #222;
				text-decoration: none;

				&::before {
					content: "";
					display: inline-block;
					width: 5px;
					height: 5px;
					border-radius: 50%;
					background-color: var(--color);
					margin-right: 10px;
					flex-shrink: 0;
					opacity: 0.6;
				}
			}

			li {
				list-style: none;
				margin: 0;
				padding: 4px 12px;
				display: flex;
				flex-direction: column;
				border-bottom: 1px solid #f0f0f0;
				transition: background-color 200ms ease;

				&:last-child {
					border-bottom: none;
				}

				&:hover {
					background-color: #f8f8f8;
				}

				ul {
					padding: 2px 0 4px 20px;
					gap: 0;

					li {
						padding: 0 8px;
						border-bottom: none;

						&:hover {
							background-color: #f0f0f0;
						}

						a {
							font-size: 12px;
							font-weight: 400;
							letter-spacing: 0.3px;
							text-transform: none;
							color: #666;
							height: 30px;

							&::before {
								display: none;
							}
						}
					}
				}

				div {
					width: 100%;
					display: flex;
					justify-content: space-between;
					align-items: center;

					a {
						width: calc(100% - 36px);
					}

					.toggleSub {
						transform: rotate(180deg);
						transition: transform 300ms ease;
						cursor: pointer;
						opacity: 0.4;
						width: 22px;
						height: 22px;

						&:hover {
							opacity: 1;
						}
					}
				}
			}

			li.collapsed {
				div {
					.toggleSub {
						transform: rotate(0deg);
					}
				}

				ul {
					display: none;
					opacity: 0;
				}
			}
		}
	}

	.menuOverlay {
		width: 100%;
		height: 100%;
		z-index: 9;
		position: fixed;
		left: 0px;
		top: 0px;
		background-color: #000;
		opacity: 0.45;
	}

	@media screen and (max-width: 768px) {
		.menuDropdown {
			width: calc(100% - 20px);

			ul {
				a {
					font-size: 15px;
					height: 44px;
				}

				li {
					padding: 6px 12px;

					ul li a {
						font-size: 14px;
						height: 40px;
					}
				}
			}
		}

		.menu .container {
			padding: 0 8px;

			a {
				font-size: 14px;
				padding: 0 6px;
			}
		}
	}

	@media screen and (max-width: 1280px) {
		.menu .other {
			display: none;
		}
	}
</style>
